'use strict';
import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiDelete, readToken } from '../api/client.js';

const MAX_FILE_MB = 10;    // per file
const MAX_TOTAL_MB = 100;  // total across the project (both groups share this)
const MAX_SAMPLES = 50;    // max files per project
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/avif';
const ALLOWED_TYPES = new Set(ACCEPT.split(','));
const ACCEPT_LABEL = 'PNG, JPG, GIF, WebP, AVIF';

const GROUPS = [
  {
    kind: 'brand_logo',
    title: 'Brand logo',
    hint: 'Your official icon/logo mark. Drawn in every post header instead of a letter monogram. Upload one square PNG (transparent background is fine); the most recent upload is used.',
  },
  {
    kind: 'product_screenshot',
    title: 'Product screenshots',
    hint: 'Real screens of your app. The AI references these in the copy and can show them inside post slides.',
  },
  {
    kind: 'brand_reference',
    title: 'Brand references',
    hint: 'Style inspiration — example posts, brand photos, mood. Steers the look & tone only; never shown as product UI.',
  },
];

async function uploadSample(projectKey, file, kind) {
  const res = await fetch(`/api/projects/${encodeURIComponent(projectKey)}/samples`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Sample-Filename': encodeURIComponent(file.name),
      'X-Sample-Kind': kind,
      Authorization: `Bearer ${readToken()}`,
    },
    body: file,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Upload failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function ProjectSamples({ projectKey }) {
  const qc = useQueryClient();

  const samplesQ = useQuery({
    queryKey: ['project-samples', projectKey],
    queryFn: () => apiGet(`/projects/${encodeURIComponent(projectKey)}/samples`),
  });

  const deleteMut = useMutation({
    mutationFn: (sampleId) =>
      apiDelete(`/projects/${encodeURIComponent(projectKey)}/samples/${sampleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-samples', projectKey] }),
  });

  const samples = samplesQ.data?.samples || [];
  const totalCount = samples.length;
  const totalBytes = samples.reduce((sum, s) => sum + (s.file_size || 0), 0);
  const refresh = () => qc.invalidateQueries({ queryKey: ['project-samples', projectKey] });

  return (
    <section className="post-section">
      <div className="section-h-row">
        <h2>Brand visuals</h2>
        <span className="muted">{totalCount} / {MAX_SAMPLES} · {(totalBytes / 1024 / 1024).toFixed(1)} / {MAX_TOTAL_MB} MB</span>
      </div>
      <p className="muted small sample-hint">{ACCEPT_LABEL} · up to {MAX_FILE_MB} MB each, {MAX_TOTAL_MB} MB total across both groups.</p>

      {samplesQ.isLoading && <div className="loading">Loading…</div>}

      {GROUPS.map((g) => (
        <SampleGroup
          key={g.kind}
          projectKey={projectKey}
          kind={g.kind}
          title={g.title}
          hint={g.hint}
          samples={samples.filter((s) => (s.kind || 'product_screenshot') === g.kind)}
          totalCount={totalCount}
          totalBytes={totalBytes}
          onRefresh={refresh}
          onDelete={(id) => deleteMut.mutate(id)}
          deleting={deleteMut.isPending}
        />
      ))}
    </section>
  );
}

function SampleGroup({ projectKey, kind, title, hint, samples, totalCount, totalBytes, onRefresh, onDelete, deleting }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(null);

  async function handleFiles(fileList) {
    setUploadError(null);
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const FILE_CAP = MAX_FILE_MB * 1024 * 1024;
    const TOTAL_CAP = MAX_TOTAL_MB * 1024 * 1024;

    const problems = [];
    const valid = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.has(f.type)) { problems.push(`${f.name}: unsupported type (allowed: ${ACCEPT_LABEL})`); continue; }
      if (f.size > FILE_CAP) { problems.push(`${f.name}: over ${MAX_FILE_MB} MB`); continue; }
      valid.push(f);
    }

    setUploading(true);
    setProgress({ done: 0, total: valid.length });
    let runningBytes = totalBytes;   // project-wide caps are shared across groups
    let count = totalCount;
    let done = 0;
    for (const f of valid) {
      if (count >= MAX_SAMPLES) { problems.push(`Skipped ${f.name}: max ${MAX_SAMPLES} files per project`); continue; }
      if (runningBytes + f.size > TOTAL_CAP) { problems.push(`Skipped ${f.name}: would exceed ${MAX_TOTAL_MB} MB total`); continue; }
      try {
        await uploadSample(projectKey, f, kind);
        runningBytes += f.size;
        count += 1;
        done += 1;
        setProgress({ done, total: valid.length });
      } catch (err) {
        problems.push(`${f.name}: ${err.message || 'upload failed'}`);
      }
    }

    onRefresh();
    setUploading(false);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (problems.length) setUploadError(problems.join(' · '));
  }

  return (
    <div className="sample-group">
      <div className="section-h-row">
        <h3 className="sample-group-title">{title}</h3>
        <span className="muted">{samples.length}</span>
      </div>
      <p className="muted small sample-hint">{hint}</p>

      <div
        className={`sample-drop-zone${dragOver ? ' drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading
          ? <span className="muted">{progress ? `Uploading ${progress.done} of ${progress.total}…` : 'Uploading…'}</span>
          : <span className="muted">{dragOver ? 'Drop to upload' : 'Click or drag images here — you can select several at once'}</span>
        }
      </div>

      {uploadError && <div className="error sample-error">{uploadError}</div>}

      {samples.length > 0 && (
        <div className="sample-grid">
          {samples.map((s) => (
            <div key={s.id} className="sample-card">
              {s.file_url
                ? <img src={s.file_url} alt={s.filename} className="sample-thumb" loading="lazy" />
                : <div className="sample-thumb sample-thumb-placeholder" />
              }
              <div className="sample-meta">
                <span className="sample-name" title={s.filename}>{s.filename}</span>
                <button
                  className="sample-delete"
                  onClick={() => onDelete(s.id)}
                  disabled={deleting}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
